import { Geolocation, type Position } from '@capacitor/geolocation';
import { Capacitor } from '@capacitor/core';
import { supabase } from './supabaseClient';

type GeoPositionOptions = {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
};

// Margine oltre il timeout entro cui un fix è considerato "fresco". Su Android,
// con i servizi di localizzazione SPENTI ma permesso concesso, getCurrentPosition
// può restituire l'ultima posizione nota dalla cache di sistema ignorando
// maximumAge: 0. Un fix ottenuto davvero durante la chiamata ha timestamp ~adesso
// (entro il timeout); uno in cache da GPS spento è molto più vecchio. Questa
// soglia separa i due casi senza falsi negativi su un GPS realmente attivo.
const STALE_FIX_GUARD_MS = 10000;

export const geolocationService = {
  /**
   * Legge lo stato permessi senza mostrare prompt. L'app lavora con la
   * posizione APPROSSIMATIVA: basta che una delle due autorizzazioni
   * (precisa o approssimativa) sia concessa.
   */
  async checkPermissions() {
    if (!Capacitor.isNativePlatform()) return 'granted';
    const status = await Geolocation.checkPermissions();
    if (status.location === 'granted' || status.coarseLocation === 'granted') return 'granted';
    return status.coarseLocation ?? status.location;
  },

  /**
   * Richiede permessi al volo (solo su mobile). Si chiede SOLO la posizione
   * approssimativa (alias coarseLocation): su Android 12+ il dialogo di
   * sistema propone unicamente "Approssimativa" — l'app non ha bisogno di
   * quella precisa.
   */
  async requestPermissions() {
    if (!Capacitor.isNativePlatform()) return 'granted';
    const status = await Geolocation.checkPermissions();
    if (status.location === 'granted' || status.coarseLocation === 'granted') return 'granted';
    const req = await Geolocation.requestPermissions({ permissions: ['coarseLocation'] });
    if (req.location === 'granted' || req.coarseLocation === 'granted') return 'granted';
    return req.coarseLocation ?? req.location;
  },

  /**
   * Apre le impostazioni di sistema dell'app: unico modo per riattivare la
   * geolocalizzazione quando il permesso è stato negato in modo permanente
   * (Android non mostra più il dialogo di richiesta).
   */
  async openSettings() {
    if (!Capacitor.isNativePlatform()) return;
    try {
      const { registerPlugin } = await import('@capacitor/core');
      const bg = registerPlugin<{ openSettings(): Promise<void> }>('BackgroundGeolocation');
      await bg.openSettings();
    } catch (e) {
      console.warn('[geo] openSettings non disponibile', e);
    }
  },

  /** Posizione attuale con timeout. Fallback a `navigator.geolocation` su web. */
  async getCurrentPosition(options: GeoPositionOptions = {}): Promise<Position> {
    const resolvedOptions = {
      enableHighAccuracy: false, // sufficiente per la nostra app, risparmia batteria
      timeout: 15000,
      maximumAge: 60000,
      ...options,
    };
    if (Capacitor.isNativePlatform()) {
      return await Geolocation.getCurrentPosition(resolvedOptions);
    }
    // Web fallback (Vite dev server)
    return new Promise((resolve, reject) => {
      if (!('geolocation' in navigator)) {
        reject(new Error('Geolocalizzazione non supportata su questo browser.'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (p) =>
          resolve({
            coords: {
              latitude: p.coords.latitude,
              longitude: p.coords.longitude,
              accuracy: p.coords.accuracy,
              altitude: p.coords.altitude,
              altitudeAccuracy: p.coords.altitudeAccuracy,
              heading: p.coords.heading,
              speed: p.coords.speed,
            },
            timestamp: p.timestamp,
          } as Position),
        (e) => reject(e),
        resolvedOptions
      );
    });
  },

  /** Verifica rapida GPS/location services; opzionalmente richiede il permesso. */
  async probeCurrentPosition(options: GeoPositionOptions & { requestPermission?: boolean } = {}): Promise<Position> {
    const status = options.requestPermission
      ? await this.requestPermissions()
      : await this.checkPermissions();
    if (status !== 'granted') {
      throw new Error('geolocation_denied');
    }
    const timeout = options.timeout ?? 2500;
    const maximumAge = options.maximumAge ?? 0;
    const position = await this.getCurrentPosition({
      timeout,
      maximumAge,
      enableHighAccuracy: options.enableHighAccuracy ?? false,
    });

    // Android: con GPS spento ma permesso concesso, getCurrentPosition può
    // risolvere con l'ULTIMA posizione nota (cache di sistema) ignorando
    // maximumAge: 0, facendo credere che il GPS sia attivo. Verifichiamo quindi
    // l'età del fix: se supera il massimo consentito (maximumAge + margine), è un
    // fix stantìo → il GPS non è realmente attivo, lo trattiamo come errore così
    // il banner di geolocalizzazione compare correttamente.
    const fixAge = Date.now() - Number(position.timestamp ?? 0);
    const maxFixAge = Math.max(maximumAge, timeout + STALE_FIX_GUARD_MS);
    if (Number.isFinite(fixAge) && fixAge > maxFixAge) {
      throw new Error('geolocation_stale_fix');
    }
    return position;
  },

  /**
   * Salva la posizione approssimata (privacy-first) via funzione RPC.
   * `source` distingue un fix GPS reale da una posizione derivata
   * dall'indirizzo del profilo: il server la usa per capire se l'ultima
   * posizione è "viva" (utente online col GPS attivo) o se, a utente offline,
   * deve ripiegare sull'indirizzo predefinito.
   */
  async saveMyLocation(
    latitude: number,
    longitude: number,
    postalCode?: string,
    countryCode?: string,
    source: 'gps' | 'address' = 'gps',
  ) {
    // Approssimo arrotondando a 2 decimali (~1 km di precisione). Vedi suggerimento privacy nel doc.
    const approxLat = Math.round(latitude * 100) / 100;
    const approxLon = Math.round(longitude * 100) / 100;
    const { error } = await supabase.rpc('update_my_location', {
      p_latitude: approxLat,
      p_longitude: approxLon,
      p_postal_code: postalCode ?? null,
      p_country_code: countryCode ?? null,
      p_source: source,
    });
    if (error) {
      // Backward-compatibility: database non ancora migrato (funzione a 4
      // parametri, senza p_source) → si ritenta con la firma precedente.
      // Il match è volutamente STRETTO su 'p_source': l'errore PostgREST di
      // funzione non trovata elenca i parametri nominati (incluso p_source),
      // mentre gli errori applicativi della RPC ("Latitudine non valida", …)
      // non lo contengono e NON devono innescare un retry che li maschera.
      const message = String((error as { message?: unknown })?.message ?? '').toLowerCase();
      if (message.includes('p_source')) {
        const { error: legacyError } = await supabase.rpc('update_my_location', {
          p_latitude: approxLat,
          p_longitude: approxLon,
          p_postal_code: postalCode ?? null,
          p_country_code: countryCode ?? null,
        });
        if (legacyError) throw legacyError;
        return;
      }
      throw error;
    }
  },

  async refreshAndSave() {
    const status = await this.requestPermissions();
    if (status === 'denied') {
      throw new Error('geolocation_denied');
    }
    const pos = await this.getCurrentPosition();
    await this.saveMyLocation(pos.coords.latitude, pos.coords.longitude, undefined, undefined, 'gps');
    return pos;
  },

  /** Geocodifica un indirizzo testuale via Nominatim → lat/lon approssimati. */
  async geocodeAddress(address: string): Promise<{ lat: number; lon: number } | null> {
    try {
      const url =
        'https://nominatim.openstreetmap.org/search?' +
        new URLSearchParams({
          q: address,
          format: 'json',
          limit: '1',
          'accept-language': 'it,en',
        }).toString();
      const res = await fetch(url, { headers: { 'User-Agent': 'SwapWish/1.0' } });
      if (!res.ok) return null;
      const data = await res.json();
      if (!Array.isArray(data) || data.length === 0) return null;
      return { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) };
    } catch {
      return null;
    }
  },
};
